/**
 * StudioWebRenderer.tsx — Renderizador web do Studio
 *
 * Monta a árvore de blocos do documento JSON usando componentes React reais do
 * sistema (e não uma string de HTML), o que permite blocos interativos como o
 * aceite da proposta.
 *
 * Também suporta o modo HTML bruto: o HTML importado é exibido como está e, nos
 * pontos marcados com `data-uaizy-block="chave"` (System Slots), o comportamento
 * autorizado do sistema é injetado. Nenhum JavaScript do HTML é executado.
 *
 * RELEASE: v1.5.0 - 2026-09-11 - Exporta o renderizador canônico de um nó para
 *            o canvas reutilizar os mesmos envoltórios da página.
 * RELEASE: v1.4.0 - 2026-09-11 - Direção de arte por tela: visibilidade e ordem
 *            por tamanho de tela (motor de composições, Fase 7)
 * RELEASE: v1.3.0 - 2026-09-11 - Renova o orçamento de interação a cada desenho
 *            do documento (motor de composições, Fase 5)
 * RELEASE: v1.2.0 - 2026-09-11 - Efeitos visuais por registro versionado (Fase 4)
 * RELEASE: v1.1.0 - 2026-09-11 - Camadas e posicionamento governado (Fase 3)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 2)
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { StudioBlockNode, StudioDocument } from '@/lib/studio/core';
import { classeDoEfeito, lerEfeito } from '@/lib/studio/core/effects';
import { classesDoDecor, temDecor, variaveisDoDecor } from '@/lib/studio/core/decor';
import { classeDeVisibilidade, estiloDaOrdem, temRegraDeTela } from '@/lib/studio/core/screen';
import { reiniciarOrcamentoDeMovimento } from './primitiveBlocks';
import { classesDaCamada, temCamada, variaveisDaCamada } from '@/lib/studio/core/layout/layers';
import { StudioWebProvider, type StudioWebContextValue } from './StudioWebContext';
import { getWebBlock } from './webBlockRegistry';

/** Renderiza um nó e, recursivamente, seus filhos. */
export function StudioWebNode({ node }: { node: StudioBlockNode }) {
  const Component = getWebBlock(node.type);
  // Bloco desconhecido nunca derruba a página: apenas não é exibido.
  if (!Component) return null;

  const children = node.children?.length ? (
    <>
      {node.children.map((filho) => (
        <StudioWebNode key={filho.id} node={filho} />
      ))}
    </>
  ) : undefined;

  let conteudo = <Component node={node}>{children}</Component>;

  // Efeito visual (Fase 4): o editor desenha o mesmo efeito da página publicada.
  if (lerEfeito(node.props)) {
    const classeEfeito = classeDoEfeito(node.props, 'editor');
    if (classeEfeito) conteudo = <div className={classeEfeito}>{conteudo}</div>;
  }

  // Fundo decorativo e acabamento de borda (biblioteca própria, Fase 1):
  // o editor desenha exatamente o mesmo da página publicada.
  if (temDecor(node.props)) {
    const classeDecor = classesDoDecor(node.props, 'editor');
    if (classeDecor) {
      conteudo = (
        <div className={classeDecor} style={variaveisDoDecor(node.props) as React.CSSProperties}>
          {conteudo}
        </div>
      );
    }
  }


  // Direção de arte (Fase 7): visibilidade por tela e ordem dentro da faixa.
  // No editor tudo continua desenhado; quem esconde é o CSS do tamanho de tela.
  if (temRegraDeTela(node.props)) {
    const classeTela = classeDeVisibilidade(node.props, 'editor');
    const ordem = estiloDaOrdem(node.props);
    if (classeTela || ordem) {
      const estiloOrdem: React.CSSProperties = {};
      ordem.split(';').forEach((par) => {
        const [chave, valor] = par.split(':');
        if (chave && valor) (estiloOrdem as Record<string, string>)[chave] = valor;
      });
      conteudo = (
        <div className={classeTela || undefined} style={ordem ? estiloOrdem : undefined}>
          {conteudo}
        </div>
      );
    }
  }

  // Camada (Fase 3): só entra quando o bloco pediu posicionamento. Sem o grupo
  // `layer`, nada é envolvido e o desenho continua idêntico ao de antes.
  if (!temCamada(node.props)) return conteudo;
  return (
    <div
      className={classesDaCamada(node.props)}
      style={variaveisDaCamada(node.props) as React.CSSProperties}
    >
      {conteudo}
    </div>
  );
}

/** Renderiza a lista de blocos do documento. */
function BlockTree({ blocks }: { blocks: StudioBlockNode[] }) {
  // Orçamento de interação (Fase 5): o teto de animações vale por documento,
  // então é renovado sempre que a árvore inteira é desenhada.
  reiniciarOrcamentoDeMovimento();
  return (
    <>
      {blocks.map((node) => (
        <StudioWebNode key={node.id} node={node} />
      ))}
    </>
  );
}

/**
 * Modo HTML bruto com System Slots.
 * Cada elemento com `data-uaizy-block` recebe, via portal, o componente
 * correspondente registrado no sistema.
 */
function RawHtmlWithSlots({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [slots, setSlots] = useState<Array<{ key: string; element: HTMLElement }>>([]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const encontrados: Array<{ key: string; element: HTMLElement }> = [];
    root.querySelectorAll<HTMLElement>('[data-uaizy-block]').forEach((el) => {
      const key = el.dataset.uaizyBlock;
      if (!key || !getWebBlock(key)) return;
      el.innerHTML = '';
      encontrados.push({ key, element: el });
    });
    setSlots(encontrados);
  }, [html]);

  return (
    <>
      <div
        ref={containerRef}
        // HTML já sanitizado no servidor ao salvar/publicar o modelo
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {slots.map(({ key, element }, index) => {
        const Component = getWebBlock(key)!;
        return createPortal(
          <Component node={{ id: `slot-${index}`, type: key, props: {} }} />,
          element,
          `slot-${index}`,
        );
      })}
    </>
  );
}

interface StudioWebRendererProps {
  /** Documento em blocos (modo estruturado). */
  document?: StudioDocument | null;
  /** HTML compilado/importado (modo HTML bruto). */
  html?: string | null;
  /** Dados e ações disponíveis para os blocos. */
  context: StudioWebContextValue;
}

/** Ponto de entrada do renderizador web. */
export function StudioWebRenderer({ document, html, context }: StudioWebRendererProps) {
  return (
    <StudioWebProvider value={{ ...context, document: context.document ?? document ?? null }}>
      {html ? <RawHtmlWithSlots html={html} /> : <BlockTree blocks={document?.blocks ?? []} />}
    </StudioWebProvider>
  );
}
